import { Fragment, useCallback, useState } from 'react';
import Table from 'shell/components/shared/table';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { BaseTableRows, HeadCell } from 'shell/components/shared/table/types';
import { noop } from 'shell/utils/noop';
import ModalInsertSchedulationData from './schedulation-modal';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { IconButton, Typography } from '@mui/material';
import { AddCircleOutline } from '@mui/icons-material';

export interface SchedulationItemVM extends BaseTableRows {
  code?: string;
  description: string;
  type?: string;
  cronExpression?: string;
  eventSourceJobName?: string;
  event?: string;
  schedulationType?: string;
  action?: string;
}

const headCells: HeadCell<SchedulationItemVM, keyof SchedulationItemVM>[] = [
  {
    id: 'schedulationType',
    label: 'Tipo',
  },
  {
    id: 'cronExpression',
    label: 'Cron Expression',
  },
  {
    id: 'description',
    label: 'Specifiche',
  },
  {
    id: 'action',
    label: '',
  },
];

interface SchedulationTableProps {
  jobName?: string;
  body: Array<SchedulationItemVM>;
  onConfirm: (values: Array<SchedulationItemVM>) => void;
  disabled?: boolean;
}

const SchedulationTable = ({
  body,
  jobName,
  onConfirm,
  disabled,
}: SchedulationTableProps): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<SchedulationItemVM>>({
    element: null,
    open: false,
  });

  const [editModal, setEditModal] = useState<AlertModal<SchedulationItemVM>>({
    element: null,
    open: false,
  });

  const [page, setPage] = useState<number>(0);

  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const onVisibilityPress = useCallback((cell): Promise<SchedulationItemVM> => {
    return new Promise((resolve) => {
      setEditModal({ element: cell, open: true, disabled: true });
      resolve(cell);
    });
  }, []);

  const onEditPress = useCallback((cell): Promise<SchedulationItemVM> => {
    return new Promise((resolve) => {
      resolve(cell);
      setEditModal({ element: cell, open: true });
    });
  }, []);

  const onTrashPress = useCallback(
    (cell): Promise<SchedulationItemVM> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<SchedulationItemVM>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleChangeRowsPerPage = useCallback(
    (rows) => {
      setRowsPerPage(rows);
    },
    [setRowsPerPage]
  );

  const onAddBtnClick = useCallback(() => {
    setEditModal({ element: null, open: true });
  }, [setEditModal]);

  const onCloseDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  const onYesDeleteAlertModal = useCallback(() => {
    let slicedElements = body.slice();
    const values = { ...deleteAlertModal.element };

    if (
      slicedElements.find(
        (item) => item?.description.replaceAll(' ', '') === values?.description?.replaceAll(' ', '')
      )
    ) {
      slicedElements = slicedElements.filter(
        (item) => item.description.replaceAll(' ', '') !== values?.description?.replaceAll(' ', '')
      );

      onConfirm(slicedElements);
    }
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal, deleteAlertModal.element]);

  const onNoDeleteAlertModal = useCallback(() => {
    setDeleteAlertModal({ open: false, element: null });
  }, [setDeleteAlertModal]);

  return (
    <Fragment>
      <div className="pmi-d-flex pmi-justify-content-between">
        <PmiTitleDivider>Schedulazione</PmiTitleDivider>
        <span>
          <IconButton
            onClick={onAddBtnClick}
            aria-label="Aggiungi Parametro"
            aria-haspopup="true"
            disabled={disabled}
          >
            <AddCircleOutline />
          </IconButton>
          <Typography color="secondary" display="inline-block">
            Aggiungi elemento
          </Typography>
        </span>
      </div>
      <Table
        head={headCells}
        onSort={noop}
        orders={{}}
        page={page}
        rowsPerPage={rowsPerPage}
        body={body}
        onVisibilityPress={onVisibilityPress}
        onEditPress={onEditPress}
        onTrashPress={onTrashPress}
        count={body.length}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        ariaLabel="Lista Schedulazione"
        noSort
      />
      <DeleteAlertModal
        title="Cancellazione schedulazione"
        message="Confermare la cancellazione?"
        open={deleteAlertModal.open}
        onClose={onCloseDeleteAlertModal}
        onYes={onYesDeleteAlertModal}
        onNo={onNoDeleteAlertModal}
      />
      {editModal.open ? (
        <ModalInsertSchedulationData
          jobName={jobName}
          open={editModal.open}
          modalData={editModal.element}
          disabled={editModal.disabled}
          onClose={() => {
            setEditModal({ element: null, open: false });
          }}
          onSubmit={(values) => {
            let slicedElements = body.slice();

            if (
              slicedElements.find(
                (item) =>
                  item.description.replaceAll(' ', '') === values.description?.replaceAll(' ', '')
              )
            ) {
              slicedElements = slicedElements.map((item) =>
                item.description.replaceAll(' ', '') === values.description?.replaceAll(' ', '')
                  ? { ...item, ...values }
                  : item
              );

              onConfirm(slicedElements);
              setEditModal({ element: null, open: false });
              return;
            }

            slicedElements.push(values);
            setEditModal({ element: null, open: false });
            onConfirm(slicedElements);
          }}
        />
      ) : undefined}
    </Fragment>
  );
};

SchedulationTable.displayName = 'Schedulation List';

export default SchedulationTable;
